import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download, Loader2, FileSpreadsheet, FileJson, FileText, Boxes, Folder, File } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils/cn'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase/client'
import { fetchFormGroups } from '@/features/form-builder/api/form-builder-api'
import type { ExportSource } from '@/features/form-builder/api/form-io-core'
import { FormPicker } from '../components/form-picker'
import { loadPublishedDefinition } from '../api/forms'
import type { ExportFormat, ExportLayout, HeaderStrategy } from '../api/data-export-api'
import { runDataExport, type DataScope, type MultiXlsxMode } from '../api/bulk-export-api'
import type { DataIoForm } from '../api/forms'

export function DataExportPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { toast } = useToast()

  const [scope, setScope] = useState<DataScope>('form')
  const [form, setForm] = useState<DataIoForm | null>(null)
  const [groupId, setGroupId] = useState<string>('')
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())

  const [includeData, setIncludeData] = useState(true)
  const [includeSchema, setIncludeSchema] = useState(false)

  const [layout, setLayout] = useState<ExportLayout>('long')
  const [format, setFormat] = useState<ExportFormat>('csv')
  const [headerStrategy, setHeaderStrategy] = useState<HeaderStrategy>('field_key')
  const [xlsxMode, setXlsxMode] = useState<MultiXlsxMode>('workbook')
  const [schemaSource, setSchemaSource] = useState<ExportSource>('published')
  const [busy, setBusy] = useState(false)

  const { data: project } = useQuery({
    queryKey: ['project-name', projectId],
    queryFn: async () => {
      const { data, error } = await supabase.from('projects').select('name').eq('id', projectId!).single()
      if (error) throw error
      return data
    },
    enabled: !!projectId,
  })

  const { data: groups = [] } = useQuery({
    queryKey: ['form-groups', projectId],
    queryFn: () => fetchFormGroups(projectId!),
    enabled: !!projectId,
  })

  // Column list only matters for single-form data export. Tolerate unpublished
  // forms (no definition → export all columns).
  const { data: def, isLoading: defLoading } = useQuery({
    queryKey: ['data-io-def', form?.id],
    queryFn: async () => {
      const d = await loadPublishedDefinition(form!.id)
      setSelectedKeys(new Set(d.fields.map((f) => f.field_key)))
      return d
    },
    enabled: !!form && scope === 'form' && includeData,
    retry: false,
  })

  const exportable = (def?.fields ?? []).filter((f) => f.field_type !== 'repeatable_array')
  const isMultiForm = scope !== 'form'

  const toggleKey = (k: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      next.has(k) ? next.delete(k) : next.add(k)
      return next
    })
  }

  const scopeLabel =
    scope === 'project'
      ? project?.name ?? 'کل پروژه'
      : scope === 'group'
        ? groups.find((g) => g.id === groupId)?.title ?? 'گروه'
        : form?.title ?? 'فرم'

  const canExport = (() => {
    if (busy) return false
    if (!includeData && !includeSchema) return false
    if (scope === 'form' && !form) return false
    if (scope === 'group' && !groupId) return false
    // Single-form column subset: require ≥1 column only when the field list loaded.
    if (includeData && scope === 'form' && def && selectedKeys.size === 0) return false
    return true
  })()

  const doExport = async () => {
    if (!projectId || !canExport) return
    setBusy(true)
    try {
      const summary = await runDataExport({
        projectId,
        scope,
        groupId: scope === 'group' ? groupId : undefined,
        formId: scope === 'form' ? form?.id : undefined,
        includeData,
        includeSchema,
        format,
        layout,
        headerStrategy,
        xlsxMode,
        selectedKeys: def ? [...selectedKeys] : undefined,
        schemaSource,
        projectName: project?.name,
        scopeLabel,
      })
      const parts: string[] = []
      if (includeData) parts.push(`${summary.formsWithData} فرم داده‌دار، ${summary.totalRows} ردیف`)
      if (includeSchema) parts.push(`ساختار ${summary.schemaForms} فرم`)
      if (summary.formsSkipped > 0) parts.push(`${summary.formsSkipped} فرم بدون داده رد شد`)
      toast({
        title: summary.zipped ? 'بستهٔ ZIP آماده شد' : 'خروجی آماده شد',
        description: parts.join(' · ') || summary.filename,
      })
    } catch (e) {
      toast({ variant: 'destructive', title: 'خطا در خروجی', description: (e as Error).message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="خروجی"
        breadcrumbs={[{ label: 'ورود و خروجی داده', href: `/projects/${projectId}/data-io` }, { label: 'خروجی' }]}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* 1. Scope */}
          <Section title="۱. دامنهٔ خروجی">
            <div className="grid grid-cols-3 gap-2">
              <Opt active={scope === 'form'} onClick={() => setScope('form')} icon={File} title="یک فرم" desc="فرم انتخابی" />
              <Opt active={scope === 'group'} onClick={() => setScope('group')} icon={Folder} title="یک گروه" desc="همهٔ فرم‌های گروه" />
              <Opt active={scope === 'project'} onClick={() => setScope('project')} icon={Boxes} title="کل پروژه" desc="همهٔ فرم‌ها" />
            </div>

            {scope === 'form' && projectId && (
              <div className="mt-3">
                <FormPicker projectId={projectId} value={form?.id ?? ''} onChange={setForm} publishedOnly={false} />
              </div>
            )}
            {scope === 'group' && (
              <div className="mt-3">
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger>
                    <SelectValue placeholder="یک گروه را انتخاب کنید" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.title}
                        <span className="text-xs text-muted-foreground ms-2">{g.forms.length} فرم</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </Section>

          {/* 2. Content */}
          <Section title="۲. محتوای خروجی">
            <div className="space-y-2">
              <ContentToggle
                checked={includeData}
                onChange={setIncludeData}
                icon={FileSpreadsheet}
                title="داده‌های ثبت‌شده"
                desc="پاسخ‌های فرم به CSV یا Excel — قابل ویرایش و ورود مجدد"
              />
              <ContentToggle
                checked={includeSchema}
                onChange={setIncludeSchema}
                icon={FileJson}
                title="ساختار فرم (JSON)"
                desc="گروه‌ها، فرم‌ها، فیلدها، شرط‌ها و اعتبارسنجی‌ها — برای ورود به پروژهٔ دیگر"
              />
            </div>
            {includeData && includeSchema && (
              <p className="text-xs text-muted-foreground mt-2">
                هر دو مورد در یک فایل ZIP بسته‌بندی می‌شوند.
              </p>
            )}
          </Section>

          {/* 3. Data options */}
          {includeData && (
            <Section title="۳. تنظیمات داده">
              {/* Single-form column picker */}
              {scope === 'form' && form && (
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">ستون‌ها</p>
                  {defLoading ? (
                    <p className="text-sm text-muted-foreground">در حال بارگذاری فیلدها…</p>
                  ) : exportable.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      فهرست فیلد در دسترس نیست؛ همهٔ ستون‌ها صادر می‌شوند.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto rounded-lg border border-border p-3">
                      {exportable.map((f) => (
                        <label key={f.field_key} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={selectedKeys.has(f.field_key)} onCheckedChange={() => toggleKey(f.field_key)} />
                          <span>{f.label?.fa || f.field_key}</span>
                          <span className="text-xs font-mono text-muted-foreground">{f.field_key}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <p className="text-sm font-medium mb-2">چیدمان</p>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <Opt active={layout === 'long'} onClick={() => setLayout('long')} title="بلند (هر نمونه یک ردیف)" desc="یک ردیف برای هر نمونهٔ فرم" />
                <Opt active={layout === 'wide'} onClick={() => setLayout('wide')} title="عریض (هر رکورد یک ردیف)" desc="نمونه‌ها ستون‌های پسونددار" />
              </div>

              <p className="text-sm font-medium mb-2">قالب فایل</p>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <Opt active={format === 'csv'} onClick={() => setFormat('csv')} icon={FileText} title="CSV" desc={isMultiForm ? 'هر فرم یک فایل، در ZIP' : 'سازگار با Excel'} />
                <Opt active={format === 'xlsx'} onClick={() => setFormat('xlsx')} icon={FileSpreadsheet} title="Excel (xlsx)" desc="با برگهٔ اطلاعات" />
              </div>

              {/* Multi-form XLSX layout choice */}
              {isMultiForm && format === 'xlsx' && (
                <>
                  <p className="text-sm font-medium mb-2">ساختار فایل Excel</p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <Opt active={xlsxMode === 'workbook'} onClick={() => setXlsxMode('workbook')} title="یک فایل، چند برگه" desc="هر فرم یک برگه (sheet)" />
                    <Opt active={xlsxMode === 'separate'} onClick={() => setXlsxMode('separate')} title="فایل‌های جدا در ZIP" desc="هر فرم یک فایل Excel" />
                  </div>
                </>
              )}

              <p className="text-sm font-medium mb-2">سرستون</p>
              <div className="grid grid-cols-2 gap-2">
                <Opt active={headerStrategy === 'field_key'} onClick={() => setHeaderStrategy('field_key')} title="کلید فیلد" desc="مناسب ورود مجدد" />
                <Opt active={headerStrategy === 'label'} onClick={() => setHeaderStrategy('label')} title="برچسب" desc="خوانا برای انسان" />
              </div>
            </Section>
          )}

          {/* 4. Schema options */}
          {includeSchema && (
            <Section title={`${includeData ? '۴' : '۳'}. منبع ساختار`}>
              <div className="grid grid-cols-2 gap-2">
                <Opt active={schemaSource === 'published'} onClick={() => setSchemaSource('published')} title="نسخهٔ منتشرشده" desc="تعریف پایدارِ در حال استفاده" />
                <Opt active={schemaSource === 'draft'} onClick={() => setSchemaSource('draft')} title="پیش‌نویس" desc="آخرین تغییرات منتشرنشده" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                اگر منبع انتخابی برای فرمی موجود نباشد، منبع دیگر استفاده می‌شود.
              </p>
            </Section>
          )}

          <Button onClick={doExport} disabled={!canExport} className="gap-1.5 w-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            دانلود خروجی
          </Button>
        </div>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground mb-2">{title}</h3>
      {children}
    </div>
  )
}

function Opt({
  active,
  onClick,
  title,
  desc,
  icon: Icon,
}: {
  active: boolean
  onClick: () => void
  title: string
  desc: string
  icon?: React.ComponentType<{ className?: string }>
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-start rounded-lg border p-3 transition-colors',
        active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-muted/30',
      )}
    >
      {Icon && <Icon className={cn('w-4 h-4 mb-1', active ? 'text-primary' : 'text-muted-foreground')} />}
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
    </button>
  )
}

function ContentToggle({
  checked,
  onChange,
  title,
  desc,
  icon: Icon,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  title: string
  desc: string
  icon: React.ComponentType<{ className?: string }>
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={cn(
        'w-full text-start rounded-lg border p-3 flex items-start gap-3 transition-colors',
        checked ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-muted/30',
      )}
    >
      <Checkbox checked={checked} className="mt-0.5 pointer-events-none" />
      <Icon className={cn('w-5 h-5 shrink-0', checked ? 'text-primary' : 'text-muted-foreground')} />
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
    </button>
  )
}
